import React from "react";
import { Minus, Plus } from "lucide-react";
import { AlertFrequency, RuleType, SubscriptionInput, UserAlertSubscription } from "../../api/api";
import {
  RULE_LABELS, RULE_DESC, RULE_TYPES, RULE_FREQUENCIES, FREQ_LABELS, SEND_TIMES, WEEKDAY_LABELS,
  DEFAULT_SEND_HOUR, DEFAULT_SEND_MINUTE, DEFAULT_SEND_WEEKDAY,
  canSubscribeToRuleType, defaultInput, formatSendTime, hasSendSlot, hasVestigingFilter,
  VestigingMultiSelect, VestigingOption,
} from "../AlertRuleCard";
import { Switch } from "../ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CHANGED_OUTLINE, CHANGED_RING, Segmented, SubRow, SummaryBox, sameSet } from "./fields";
import { cn } from "@/lib/utils";

// One rule type's subscription as edited in the dialog. Flattened from
// UserAlertSubscription so every setting can be compared field by field.
export interface AlertDraft {
  enabled: boolean;
  frequency: AlertFrequency;
  send_email: boolean;
  send_hour: number;
  send_minute: number;
  send_weekday: number;
  stale_days: number;
  branch_ids: number[];
  own_only: boolean;
  partner_ids: number[];
}

export type AlertDrafts = Partial<Record<RuleType, AlertDraft>>;

// Vestiging chips switch to a dropdown above this many options.
const MAX_BRANCH_CHIPS = 8;

export function alertDraftFromSub(ruleType: RuleType, sub?: UserAlertSubscription): AlertDraft {
  const def = defaultInput(ruleType);
  return {
    enabled: sub?.enabled ?? false,
    frequency: sub?.frequency ?? def.frequency,
    send_email: sub?.send_email ?? def.send_email,
    send_hour: sub?.send_hour ?? DEFAULT_SEND_HOUR,
    send_minute: sub?.send_minute ?? DEFAULT_SEND_MINUTE,
    send_weekday: sub?.send_weekday ?? DEFAULT_SEND_WEEKDAY,
    stale_days: sub?.config?.stale_days ?? 14,
    branch_ids: sub?.config?.branch_ids ?? [],
    own_only: sub?.config?.own_only ?? false,
    partner_ids: sub?.config?.partner_ids ?? [],
  };
}

export function alertInput(ruleType: RuleType, d: AlertDraft, withPartners: boolean): SubscriptionInput {
  const config: SubscriptionInput["config"] = {};
  if (hasVestigingFilter(ruleType) && d.branch_ids.length) config.branch_ids = d.branch_ids;
  if (ruleType === "stale_vacancy") config.stale_days = d.stale_days;
  if (ruleType === "vacancy_update" && d.own_only) config.own_only = true;
  if (withPartners && d.partner_ids.length) config.partner_ids = d.partner_ids;
  return {
    enabled: d.enabled, frequency: d.frequency, config, send_email: d.send_email,
    send_hour: d.send_hour, send_minute: d.send_minute, send_weekday: d.send_weekday,
  };
}

type AlertField = keyof AlertDraft;

// Which settings of one rule differ from the saved version. A switched-off
// rule only counts the switch itself.
export function changedAlertFields(cur: AlertDraft, orig: AlertDraft): Set<AlertField> {
  const out = new Set<AlertField>();
  if (cur.enabled !== orig.enabled) out.add("enabled");
  if (!cur.enabled) return out;
  for (const k of ["frequency", "send_email", "stale_days", "own_only"] as const) {
    if (cur[k] !== orig[k]) out.add(k);
  }
  if (cur.send_hour !== orig.send_hour || cur.send_minute !== orig.send_minute) out.add("send_hour");
  if (cur.send_weekday !== orig.send_weekday) out.add("send_weekday");
  if (!sameSet(cur.branch_ids, orig.branch_ids)) out.add("branch_ids");
  if (!sameSet(cur.partner_ids, orig.partner_ids)) out.add("partner_ids");
  return out;
}

export function alertRuleTypesFor(user: { role: string; can_act_as_kam?: boolean }): RuleType[] {
  return RULE_TYPES.filter(rt => canSubscribeToRuleType(user, rt));
}

const FREQ_PHRASE: Record<AlertFrequency, string> = {
  immediate: "direct", hourly: "elk uur", daily: "dagelijks", weekly: "wekelijks", monthly: "maandelijks",
};

const WEEKDAY_SHORT: Record<number, string> = { 1: "Ma", 2: "Di", 3: "Wo", 4: "Do", 5: "Vr", 6: "Za", 7: "Zo" };

function listNl(items: string[]): string {
  return items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} en ${items[items.length - 1]}`;
}

function sentence(ruleType: RuleType, d: AlertDraft, firstName: string, branchLabel: (id: number) => string, partnerLabel: (id: number) => string): string {
  const what: Record<RuleType, string> = {
    new_vacancy: "een melding bij elke nieuwe vacature",
    vacancy_update: d.own_only ? "een melding als een eigen vacature wordt gewijzigd" : "een melding als een vacature wordt gewijzigd",
    stale_vacancy: `een melding als een vacature ${d.stale_days} dagen niet is bijgewerkt`,
    approval_reminder: "een herinnering zolang er mails op goedkeuring wachten",
  };
  let s = `${firstName} krijgt ${FREQ_PHRASE[d.frequency]} ${what[ruleType]}`;
  if (hasVestigingFilter(ruleType) && d.branch_ids.length) s += ` in ${listNl(d.branch_ids.map(branchLabel))}`;
  if (d.partner_ids.length) s += ` bij ${listNl(d.partner_ids.map(partnerLabel))}`;
  if (d.send_email) {
    s += hasSendSlot(d.frequency)
      ? `, ook per e-mail om ${formatSendTime(d.send_hour, d.send_minute)}${d.frequency === "weekly" ? ` op ${WEEKDAY_LABELS[d.send_weekday].toLowerCase()}` : ""}`
      : ", ook per e-mail";
  }
  return `${s}.`;
}

interface Props {
  userName: string;
  ruleTypes: RuleType[];
  cur: AlertDrafts;
  orig: AlertDrafts;
  onChange: (ruleType: RuleType, patch: Partial<AlertDraft>) => void;
  vestigingOptions: VestigingOption[];
  // Present for Waves roles (kam/admin): their alerts can be narrowed per partner.
  partners?: { id: number; name: string }[];
}

export function UserAlertsTab({ userName, ruleTypes, cur, orig, onChange, vestigingOptions, partners }: Props) {
  const firstName = userName.split(" ")[0] || userName;
  const branchLabel = (id: number) => vestigingOptions.find(o => o.id === id)?.label ?? `Vestiging #${id}`;
  const partnerLabel = (id: number) => partners?.find(p => p.id === id)?.name ?? `Partner #${id}`;

  if (ruleTypes.length === 0) {
    return <div className="text-sm text-waves-muted">Voor deze rol zijn geen meldingen beschikbaar.</div>;
  }

  return (
    <div className="flex flex-col">
      {ruleTypes.map(rt => {
        const d = cur[rt];
        const o = orig[rt];
        if (!d || !o) return null;
        const ch = changedAlertFields(d, o);
        const set = (patch: Partial<AlertDraft>) => onChange(rt, patch);
        const allBranchIds = vestigingOptions.map(v => v.id);
        // Selecting every vestiging means "all of them", including ones added
        // later — so store that as an empty filter (same rule as RuleCard).
        const setBranches = (ids: number[]) =>
          set({ branch_ids: allBranchIds.length > 0 && allBranchIds.every(id => ids.includes(id)) ? [] : ids });

        return (
          <div key={rt} className="grid grid-cols-1 items-start gap-3 border-t border-waves-line py-[18px] first:border-t-0 first:pt-0 sm:grid-cols-[minmax(140px,180px)_minmax(0,1fr)] sm:gap-5">
            <div className="flex flex-col gap-0.5">
              <div className={cn("text-sm font-bold", d.enabled ? "text-waves-ink" : "text-waves-muted")}>{RULE_LABELS[rt]}</div>
              <div className="text-[13px] leading-snug text-waves-muted text-pretty">{RULE_DESC[rt]}</div>
            </div>
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className={cn("flex-1 text-sm font-bold", d.enabled ? "text-waves-ink" : "text-waves-muted")}>
                  {d.enabled ? "Aan" : "Uit"}
                </span>
                <Switch
                  checked={d.enabled}
                  onCheckedChange={on => set({ enabled: on })}
                  aria-label={`${RULE_LABELS[rt]} ${d.enabled ? "uitzetten" : "aanzetten"}`}
                  className={cn(ch.has("enabled") && CHANGED_OUTLINE)}
                />
              </div>

              {d.enabled && (
                <div className="flex flex-col gap-3">
                  <SummaryBox>{sentence(rt, d, firstName, branchLabel, partnerLabel)}</SummaryBox>

                  <SubRow label="Frequentie">
                    <Segmented
                      aria-label="Frequentie"
                      options={RULE_FREQUENCIES[rt].map(f => ({ value: f, label: FREQ_LABELS[f] }))}
                      value={d.frequency}
                      onChange={f => set({ frequency: f })}
                      changed={ch.has("frequency")}
                    />
                  </SubRow>

                  {d.send_email && hasSendSlot(d.frequency) && (
                    <SubRow label="Moment">
                      <div className="flex flex-wrap items-center gap-2.5">
                        {d.frequency === "weekly" ? (
                          <Segmented
                            aria-label="Verzenddag"
                            options={Object.keys(WEEKDAY_SHORT).map(k => ({ value: Number(k), label: WEEKDAY_SHORT[Number(k)] }))}
                            value={d.send_weekday}
                            onChange={v => set({ send_weekday: v })}
                            changed={ch.has("send_weekday")}
                          />
                        ) : (
                          <span className="whitespace-nowrap text-sm text-waves-body">Elke dag om</span>
                        )}
                        <Select
                          value={formatSendTime(d.send_hour, d.send_minute)}
                          onValueChange={v => { const [h, m] = v.split(":").map(Number); set({ send_hour: h, send_minute: m }); }}
                        >
                          <SelectTrigger aria-label="Verzendtijd" className={cn("h-9 w-[110px] font-mono", ch.has("send_hour") && CHANGED_RING)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {SEND_TIMES.map(([h, m]) => (
                              <SelectItem key={formatSendTime(h, m)} value={formatSendTime(h, m)} className="font-mono">
                                {formatSendTime(h, m)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </SubRow>
                  )}

                  {rt === "stale_vacancy" && (
                    <SubRow label="Drempel">
                      <div className={cn("flex w-fit items-center gap-2 rounded-pill", ch.has("stale_days") && CHANGED_OUTLINE)}>
                        <StepButton label="Minder dagen" onClick={() => set({ stale_days: Math.max(1, d.stale_days - 1) })}><Minus /></StepButton>
                        <span className="min-w-[80px] text-center font-mono text-sm font-semibold">{d.stale_days} dagen</span>
                        <StepButton label="Meer dagen" onClick={() => set({ stale_days: Math.min(365, d.stale_days + 1) })}><Plus /></StepButton>
                      </div>
                    </SubRow>
                  )}

                  <SubRow label="E-mail">
                    <div className="flex items-center gap-2.5">
                      <Switch
                        checked={d.send_email}
                        onCheckedChange={on => set({ send_email: on })}
                        aria-label="Ook per e-mail"
                        className={cn(ch.has("send_email") && CHANGED_OUTLINE)}
                      />
                      <span className="text-sm text-waves-body">Ook per e-mail</span>
                    </div>
                  </SubRow>

                  {rt === "vacancy_update" && (
                    <SubRow label="Eigen">
                      <div className="flex items-center gap-2.5">
                        <Switch
                          checked={d.own_only}
                          onCheckedChange={on => set({ own_only: on })}
                          aria-label="Alleen eigen vacatures"
                          className={cn(ch.has("own_only") && CHANGED_OUTLINE)}
                        />
                        <span className="text-sm text-waves-body">Alleen eigen vacatures</span>
                      </div>
                    </SubRow>
                  )}

                  {hasVestigingFilter(rt) && vestigingOptions.length > 0 && (
                    <SubRow label="Vestigingen">
                      {vestigingOptions.length <= MAX_BRANCH_CHIPS ? (
                        <AllOrSome
                          label="Vestigingen"
                          options={vestigingOptions.map(v => ({ value: v.id, label: v.label }))}
                          value={d.branch_ids}
                          onChange={setBranches}
                          changed={ch.has("branch_ids")}
                        />
                      ) : (
                        <div className={cn("w-full max-w-[360px] rounded-nav", ch.has("branch_ids") && CHANGED_OUTLINE)}>
                          <VestigingMultiSelect options={vestigingOptions} selected={d.branch_ids} onChange={setBranches} />
                        </div>
                      )}
                    </SubRow>
                  )}

                  {partners && partners.length > 0 && (
                    <SubRow label="Partners">
                      <AllOrSome
                        label="Partners"
                        options={partners.map(p => ({ value: p.id, label: p.name }))}
                        value={d.partner_ids}
                        onChange={ids => set({ partner_ids: ids.length === partners.length ? [] : ids })}
                        changed={ch.has("partner_ids")}
                      />
                    </SubRow>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-pill border border-waves-line bg-white text-waves-ink hover:bg-waves-mist [&_svg]:size-3.5"
    >
      {children}
    </button>
  );
}

// Segmented filter with a leading "Alle" chip: an empty list means everything.
function AllOrSome({ label, options, value, onChange, changed }: {
  label: string;
  options: { value: number; label: string }[];
  value: number[];
  onChange: (ids: number[]) => void;
  changed: boolean;
}) {
  const ALL = -1;
  return (
    <Segmented
      multiple
      aria-label={label}
      options={[{ value: ALL, label: "Alle" }, ...options]}
      value={value.length === 0 ? [ALL] : value}
      onChange={next => {
        // Clicking "Alle" clears the filter; picking a specific one drops "Alle".
        if (next.includes(ALL) && value.length > 0) onChange([]);
        else onChange(next.filter(v => v !== ALL));
      }}
      changed={changed}
    />
  );
}
